import { useEffect, useRef, useState } from "react";
import { Bell, X } from "lucide-react";
import { api } from "../lib/api";

const POLL_MS = 15_000;
const CHIME_DATA = "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ=="; // silent placeholder; real chime below

// A short, pleasant ding generated as a sine wave (~440Hz, 250ms, fades out).
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.4);
  } catch { /* noop */ }
}

/**
 * Polls /api/dealer/activity for new "lead_submitted" events and shows a toast
 * + plays an audible chime. Browser notification is fired as well if permission
 * is already granted (no nag — the regular InstallPrompt asks for it elsewhere).
 */
export default function NewLeadToast() {
  const sinceRef = useRef(new Date().toISOString());
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const { data } = await api.get("/dealer/activity", {
          params: { since: sinceRef.current, limit: 20 },
        });
        if (cancelled) return;
        const items = data?.items || [];
        const newLeads = items.filter((e) => e.event_type === "lead_submitted");
        if (data?.latest) sinceRef.current = data.latest;
        if (newLeads.length > 0) {
          const top = newLeads[0];
          setToast(top);
          playChime();
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              new Notification("New lead — Scott Sells Chevys", {
                body: `${top.user_name || "Someone"} · ${top.detail || "submitted a form"}`,
                icon: "/icon-192.png",
                tag: "ssc-new-lead",
              });
            } catch { /* noop */ }
          }
        }
      } catch { /* silent — dealer might not be logged in */ }
    }
    const id = setInterval(poll, POLL_MS);
    poll();
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!toast) return null;

  return (
    <div
      className="fixed top-20 right-4 z-[85] max-w-sm rounded-2xl bg-white border-2 border-[#10a37f] shadow-2xl p-4 flex items-start gap-3 animate-[slideIn_0.3s_ease-out]"
      role="alert"
      data-testid="new-lead-toast"
    >
      <div className="shrink-0 w-10 h-10 rounded-xl bg-[#10a37f] flex items-center justify-center text-white">
        <Bell size={20} className="animate-pulse" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-black uppercase tracking-wider text-[#10a37f]">
          New lead just landed
        </div>
        <div className="font-extrabold text-[var(--ssc-navy)] mt-0.5 truncate">
          {toast.user_name || "Anonymous"}
        </div>
        <div className="text-sm text-[#6b7c93] truncate">
          {toast.user_phone || toast.user_email || "—"} · {toast.detail || "form submitted"}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setToast(null)}
        className="shrink-0 text-[#6b7c93] hover:text-[var(--ssc-navy)]"
        aria-label="Dismiss"
        data-testid="new-lead-toast-close"
      >
        <X size={18} />
      </button>
    </div>
  );
}

// silence dead-code lint hint
void CHIME_DATA;
